import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const sponsorshipBenefits = [
  {
    title: 'Authentic Brand Association',
    text: 'Align your brand with a multi-time national off-road champion representing courage, discipline, and performance.',
  },
  {
    title: 'High-Impact Digital Content',
    text: 'Create branded racing content, reels, photographs, behind-the-scenes stories, and sponsor integrations.',
  },
  {
    title: 'Event & Customer Engagement',
    text: 'Brand ambassador presence at launches, dealer meets, corporate events, employee engagement, and public rides.',
  },
  {
    title: 'Sports & Automotive Credibility',
    text: "Connect with India's motorsport and automotive communities through a genuine, active competitor.",
  },
  {
    title: 'Corporate Storytelling',
    text: 'Leverage motorsport achievement and corporate leadership for LinkedIn, PR, and internal communications.',
  },
  {
    title: 'Media & PR Opportunities',
    text: 'Build momentum around race participation, achievements, interviews, and brand announcements.',
  },
  {
    title: 'Sustainability & Purpose',
    text: 'Explore campaigns connecting motorsport, responsible mobility, fitness, road safety, and community initiatives.',
  },
  {
    title: 'Long-Term Partnership',
    text: 'Develop multi-season branding across race apparel, motorcycle, events, digital media, and corporate engagements.',
  },
];

const activations = [
  { title: 'Race Bike Branding', text: 'Brand placement on motorcycle, number boards, and racing equipment.' },
  { title: 'Rider Apparel', text: 'Primary or secondary logo placement on jersey, riding gear, and lifestyle apparel.' },
  { title: 'Social Media', text: 'Dedicated posts, reels, stories, sponsor mentions, and campaign content.' },
  { title: 'Corporate Engagement', text: 'Motivational talks, leadership sessions, employee rides, and customer experiences.' },
  { title: 'Brand Events', text: 'Product launches, dealer meets, exhibitions, and experiential activations.' },
  { title: 'Purpose Campaigns', text: 'Fitness, road safety, sustainability, and community-focused initiatives.' },
];

const valuePillars = [
  { title: 'Brand Visibility', tag: 'Reach', text: 'Put your brand alongside a proven champion.' },
  { title: 'Brand Engagement', tag: 'Connect', text: 'Create experiences people remember.' },
  { title: 'Brand Impact', tag: 'Purpose', text: 'Build a partnership with purpose.' },
];

const Sponsorship = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, delay: i * 0.08, ease: [0.25, 1, 0.5, 1] },
    }),
  };

  const WhatsAppIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
    </svg>
  );

  return (
    <section
      id="sponsorship"
      className="relative py-16 md:py-24 overflow-hidden bg-darker text-white"
    >
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-[#0a0505] to-black"></div>
        <div className="absolute top-1/4 left-0 w-[500px] h-[500px] rounded-full opacity-10 blur-3xl bg-primary"></div>
        <div className="absolute bottom-1/4 right-0 w-[400px] h-[400px] rounded-full opacity-5 blur-3xl bg-white"></div>
      </div>

      {/* Checkered pattern top-right */}
      <div className="absolute top-8 right-8 grid grid-cols-6 gap-[2px] opacity-20 pointer-events-none">
        {[...Array(24)].map((_, i) => (
          <div
            key={i}
            className={`w-3 h-3 ${
              (Math.floor(i / 6) + i) % 2 === 0 ? 'bg-white' : 'bg-transparent'
            }`}
          ></div>
        ))}
      </div>

      <div ref={ref} className="relative max-w-6xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="mb-10 text-center">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            className="flex items-center justify-center gap-3 mb-3"
          >
            <div className="w-8 md:w-12 h-[2px] bg-primary"></div>
            <span className="text-primary font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase">
              Partner Up
            </span>
            <div className="w-8 md:w-12 h-[2px] bg-primary"></div>
          </motion.div>

          <motion.h2
            variants={fadeUp}
            custom={1}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            className="font-heading text-3xl md:text-5xl lg:text-6xl text-white leading-[0.95] tracking-tight mb-3 font-black"
          >
            WHY SPONSOR <span className="text-primary">VISHWAS S.D.?</span>
          </motion.h2>

          <motion.p
            variants={fadeUp}
            custom={2}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            className="text-xs md:text-sm text-primary/80 font-mono tracking-widest uppercase mb-4"
          >
            Turn Champion Performance into Brand Visibility, Engagement & Impact
          </motion.p>

          <motion.p
            variants={fadeUp}
            custom={3}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            className="text-xs md:text-sm text-white/60 max-w-2xl mx-auto leading-relaxed"
          >
            Associate your brand with a proven national champion, experienced corporate professional, and high-energy personality built around measurable value and long-term purpose.
          </motion.p>
        </div>

        {/* Value Pillars Banner */}
        <motion.div
          variants={fadeUp}
          custom={4}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="mb-12"
        >
          <div className="relative bg-gradient-to-br from-primary via-red-800 to-red-950 rounded-2xl overflow-hidden shadow-xl">
            <div className="relative grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/10">
              {valuePillars.map((pillar, i) => (
                <div key={i} className="p-6 text-center">
                  <span className="inline-block text-[10px] md:text-xs tracking-[0.3em] uppercase text-white/60 font-mono mb-2">
                    · {pillar.tag} ·
                  </span>
                  <h4 className="font-heading text-lg md:text-2xl text-white tracking-wider mb-2 font-bold">
                    {pillar.title}
                  </h4>
                  <p className="text-xs md:text-sm text-white/80 leading-relaxed">
                    {pillar.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Sponsorship Benefits Grid */}
        <motion.div
          variants={fadeUp}
          custom={5}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="w-2 h-2 bg-primary"></span>
            <h3 className="font-heading text-lg md:text-xl lg:text-2xl text-white tracking-wider font-bold uppercase">
              Sponsorship Benefits
            </h3>
            <div className="flex-1 h-[1px] bg-white/10"></div>
            <span className="font-mono text-[10px] md:text-xs text-white/40 tracking-wider">
              08 REASONS
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {sponsorshipBenefits.map((item, i) => (
              <div
                key={i}
                className="bg-gradient-to-br from-[#0a0a0a] via-[#141414] to-[#0a0a0a] rounded-xl border border-white/5 p-5 hover:border-primary/40 transition-colors"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-mono text-xs text-primary font-bold">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h4 className="font-heading text-sm md:text-base text-primary tracking-wider uppercase font-bold">
                    {item.title}
                  </h4>
                </div>
                <p className="text-xs md:text-sm text-white/60 leading-relaxed pl-7">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Sponsorship Activations Grid */}
        <motion.div
          variants={fadeUp}
          custom={6}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="w-2 h-2 bg-primary"></span>
            <h3 className="font-heading text-lg md:text-xl lg:text-2xl text-white tracking-wider font-bold uppercase">
              Sponsorship Activations
            </h3>
            <div className="flex-1 h-[1px] bg-white/10"></div>
            <span className="font-mono text-[10px] md:text-xs text-white/40 tracking-wider">
              06 OPTIONS
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {activations.map((item, i) => (
              <div
                key={i}
                className="bg-gradient-to-br from-[#0a0a0a] via-[#141414] to-[#0a0a0a] rounded-xl border border-white/5 p-5 hover:border-primary/40 transition-colors"
              >
                <span className="font-mono text-[10px] text-white/30 tracking-wider block mb-2">
                  /{String(i + 1).padStart(2, '0')}
                </span>
                <h4 className="font-heading text-base text-white tracking-wide leading-tight mb-2 font-bold">
                  {item.title}
                </h4>
                <p className="text-xs text-white/50 leading-relaxed">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Contact & Instant WhatsApp Connect Section */}
        <motion.div
          id="contact"
          variants={fadeUp}
          custom={7}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="relative bg-gradient-to-br from-[#0a0a0a] via-[#141414] to-[#0a0a0a] rounded-2xl border border-white/5 overflow-hidden p-6 md:p-12 text-center"
        >
          <div className="h-1 bg-gradient-to-r from-transparent via-primary to-transparent absolute top-0 left-0 w-full"></div>

          <div className="max-w-2xl mx-auto space-y-6">
            <div>
              <h3 className="font-heading text-2xl md:text-4xl text-white leading-tight mb-3 font-bold">
                CONNECT FOR SPONSORSHIP <br />& <span className="text-primary">BRAND PARTNERSHIPS</span>
              </h3>
              <p className="text-xs md:text-sm text-white/60">
                Let's design a partnership that puts your brand where the action is. Connect directly on WhatsApp for instant enquiry and collaboration details.
              </p>
            </div>

            {/* Direct Instant WhatsApp CTA */}
            <div className="pt-2">
              <a
                href="https://wa.me/919986712035?text=Hi%20Vishwas,%20I'm%20interested%20in%20a%20sponsorship%20and%20brand%20partnership."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#25D366] hover:bg-[#20bd5a] text-black font-heading font-extrabold text-sm md:text-base tracking-wider uppercase rounded-xl transition-all duration-300 shadow-lg shadow-[#25D366]/20 hover:scale-105"
              >
                <WhatsAppIcon className="w-6 h-6" />
                <span>Chat Instantly on WhatsApp</span>
              </a>
            </div>

            {/* Additional Contact Meta */}
            <div className="pt-6 border-t border-white/10 flex flex-wrap items-center justify-center gap-6 md:gap-10 text-xs text-white/50 font-mono">
              <div>
                <span className="text-white/30 uppercase tracking-widest block text-[9px]">Email</span>
                <a href="mailto:rvishwas81@gmail.com" className="text-white hover:text-primary transition-colors">
                  rvishwas81@gmail.com
                </a>
              </div>
              <div className="hidden sm:block text-white/20">•</div>
              <div>
                <span className="text-white/30 uppercase tracking-widest block text-[9px]">Location</span>
                <span className="text-white">Bengaluru, Karnataka, India</span>
              </div>
            </div>

            <div className="p-4 bg-primary/10 border border-primary/20 rounded-xl mt-6">
              <p className="text-xs text-white/70 leading-relaxed italic max-w-xl mx-auto">
                "The right partnership isn't a logo on a bike — it's a shared story that reaches thousands who share the passion."
              </p>
              <span className="font-mono text-xs text-primary mt-2 block tracking-wider font-bold">
                — VISHWAS S.D.
              </span>
            </div>
          </div>
        </motion.div>

        {/* Bottom Tag */}
        <motion.div
          variants={fadeUp}
          custom={8}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="mt-10 flex items-center justify-center gap-3"
        >
          <div className="w-8 h-[1px] bg-white/20"></div>
          <span className="text-[10px] md:text-xs tracking-[0.3em] text-white/40 uppercase font-mono">
            Ride together · Win together
          </span>
          <div className="w-8 h-[1px] bg-white/20"></div>
        </motion.div>
      </div>
    </section>
  );
};

export default Sponsorship;